// Les modules activés par le bureau (D-122), rangés par objectifs et qui disent pourquoi les activer (D-132, maquettes
// m1 à m6 validées) :
//   #/club/modules/choisir   « Qu'est-ce qui prend le plus de temps au bureau ? » (2e des premiers pas, m1) : 7 besoins,
//                            « Voir ce qu'Arceau propose », « Continuer sans module pour l'instant », et ce que le club a
//                            dans tous les cas (le socle en 4 bénéfices) ;
//   #/club/modules/proposer  « Pour le club : N modules » (m2) : une rubrique par besoin coché, chaque module avec son
//                            pourquoi et le temps de mise en place ; les autres « à ajouter maintenant ou plus tard » ;
//   #/club/modules           Club › Modules et réglages (m3 ; D-139 : une roue par module qui a des réglages, la même
//                            page que sa ⚙) : rangés par objectif ; activés (par qui, quand), éteints (et qui les
//                            demande) ; un module éteint est caché aux membres et reste ici, prêt à être activé ;
//   fiches « Activer » (m4 : pourquoi l'activer, la mise en place, puis ce qui apparaît rôle par rôle, ce qui sera créé,
//   « Prévenir les membres » coché) et « Éteindre » (ce qui disparaît, ce que gardent les inscrits, ce qui est gardé) ;
//   la ligne « Activer » d'un module éteint dans son objectif, onglet Club du bureau (m6, `offModuleLine`) ;
//   l'écran d'un module éteint atteint par un vieux lien (« Activer » pour le bureau, « Demander au bureau » pour un coach).
// D-158 : plus aucun module allumé sans son assistant (src/ui/assistant.ts) : un module jamais activé se « Configure et
// active » au bout de son assistant ; un assistant arrêté se « Reprend » ; un module activé se « Revoit avec l'assistant » ;
// « Activer ces N modules » devient « Configurer ces N modules » (les assistants enchaînés, le plus court d'abord). Un module
// éteint après avoir été activé se rallume par sa fiche « Activer », sans assistant à refaire (ses réglages sont gardés).
import './modules.css';
import './assistant.css';
import './formation.css';
import {
  ALL_MODULE_KEYS, BASE_BENEFITS, BETA_MODULES, DEFAULT_ON, DEFAULT_REWARDS, GOALS, MODULES, MODULE_KEYS, NEEDS, activateLabel, keptLine, matchesLine, minutesLabel, proposal,
  volunteerSetup, type ModuleKey, type NeedKey, type Why,
} from '../domain/modules';
import { askModule, chooseModules, modulePreview, setModule, type ModulePreview } from '../lib/modules-api';
import { MODULE_SETTINGS, SETTINGS } from '../domain/nav';
import './nav.css';
import { clubBureau } from '../lib/recus-api';
import { clubGender, type TeamGender } from '../domain/labels';
import { parseLocalDate } from '../domain/schedule';
import { betaOpen, modOn, modState, refreshModules, type Ctx } from './context';
import { $, $$, html, mount, openSheet, raw, toast, type Html } from './dom';
import { FILMS } from '../guide/films';
import { forgetState } from '../guide/first-steps';
import { renderAssistant, hasWizard, setChain } from './assistant';
import { NO_FIGURES, WIZARDS, chainOrder, configureLabel, questionsOf, runLine, sizeLine, startedLine, stepsOf, type WizData, type WizKey } from '../domain/assistant';

const HOST_BASE = (import.meta.env.VITE_CLUB_HOST_BASE as string | undefined)?.trim() || null;
/** Le bureau du club affiché (quelle que soit la casquette : il en a le droit). */
export const isClubAdmin = (c: Ctx) => !!c.club && c.full.adminOf.some(a => a.id === c.club!.id);
/** Le genre des équipes du club, pour « Joueuses », « grande / plus jeune ». */
const genderOf = (c: Ctx): TeamGender => clubGender(c.full.teams.filter(t => t.club_id === c.club?.id));

/** Les modules de ce club : tous, plus ses bêtas ouvertes (D-141 : ailleurs, une bêta n'apparaît pas). */
export const clubModuleKeys = (c: Ctx): ModuleKey[] => ALL_MODULE_KEYS.filter(k => betaOpen(c, k));
/** Les modules d'un objectif pour ce club : ceux de l'objectif, ses bêtas ouvertes, puis ceux allumés par défaut (D-149). */
export const goalModules = (c: Ctx, goal: (typeof GOALS)[number]): ModuleKey[] =>
  [...goal.modules, ...BETA_MODULES.filter(k => MODULES[k].goal === goal.key && betaOpen(c, k)), ...DEFAULT_ON.filter(k => MODULES[k].goal === goal.key)];
/** La pastille d'un module en bêta (« Bêta · club pilote », « Inclus dans l’abonnement »). */
export const betaTags = (k: ModuleKey): Html | '' => {
  const b = MODULES[k].beta;
  return b ? html`<span class="fo-tags"><span class="fo-beta">${b.label}</span><span class="muted small">${b.price}</span></span>` : '';
};
const icon = (k: ModuleKey) => html`<span class="mi-ico" aria-hidden="true"><span class="ti ${MODULES[k].icon}"></span></span>`;
const filmBtn = (k: ModuleKey) => {
  const f = MODULES[k].film;
  return f && FILMS[f] ? html`<button type="button" class="mo-film" data-film="${k}">▶ Voir le film · ${FILMS[f]} s</button>` : '';
};
const dayFr = (iso: string) => new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' });
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** D-158 : un module jamais activé dans ce club (pas de ligne) s'active au bout de son assistant ; un module éteint après
 *  avoir été activé garde ses réglages et se rallume par sa fiche. Les skins (allumés d'office) n'ont pas d'assistant. */
export const needsWizard = (c: Ctx, k: ModuleKey): boolean => k in WIZARDS && !modOn(c, k) && !(modState(c)?.modules ?? []).some(m => m.module === k);
const wizardOf = (c: Ctx, k: ModuleKey) => modState(c)?.wizards?.find(w => w.module === k && w.state === 'open') ?? null;
/** Ce qu'il faut pour compter les questions d'un assistant sans lire la base (celles qui dépendent des données comptent). */
const sizeData = (c: Ctx): WizData => ({ club: { name: '', place: '' }, g: genderOf(c), me: { uid: c.uid, first: '' }, fig: NO_FIGURES, review: false,
  volOn: modOn(c, 'benevolat'), notice: null, host: null, needs: { roles: [], isDefault: true, units: [] }, fees: [{ id: '', name: '', cents: null }],
  entries: [{ id: '', name: '', set: false }], rewardsMissing: ['bronze'] });
export const wizSize = (c: Ctx, k: ModuleKey) => {
  const def = WIZARDS[k as WizKey], d = sizeData(c);
  return { q: questionsOf(def, d).length, n: stepsOf(def, d) };
};
/** Le geste « Activer » d'un module éteint : son assistant s'il n'a jamais été activé, sinon sa fiche. */
export function activateModule(c: Ctx, k: ModuleKey, onChange: () => void): void {
  if (needsWizard(c, k)) location.hash = `#/club/modules/assistant/${k}`;
  else void openModuleSheet(c, k, true, onChange);
}

/** Les bénéfices d'un module (ou du socle) : un picto, ce que le club y gagne, une ligne de plus (m1, m2, m4). */
export const whyList = (rows: Why[], guide = '') => html`<ul class="mo-why" ${guide ? html`data-guide="${guide}"` : ''}>${rows.map(w => html`
  <li><span class="mo-why-ic" aria-hidden="true">${w.icon}</span><span><b>${w.title}</b><span>${w.text}</span></span></li>`)}</ul>`;

/** Une ligne de module avec son interrupteur. `guide` : la cible de la visite « Activer un module ». */
/** Une ligne de module : son icône, son nom et ce qu'il apporte, l'interrupteur ; activé et avec des réglages, la roue qui
 *  ouvre la même page que sa ⚙ (D-139, « Modules et réglages »). */
const modRow = (c: Ctx, k: ModuleKey, on: boolean, sub?: Html | string, extra: Html | '' = '', guide = false, gear = false, foot: Html | '' = '') => {
  const set = gear && on ? MODULE_SETTINGS[k] : undefined;
  // D-157 : la roue des skins ouvre « Les skins du club » (les 10 que le club offre), une page de Modules et réglages.
  const href = set ? SETTINGS[set].href : gear && on && k === 'skins' ? '#/club/modules/skins' : null;
  return html`
  <li class="mo-mod ${on ? 'on' : ''} ${href ? 'nv-mo-g' : ''}">${icon(k)}
    <button type="button" class="card-main mo-main" data-mo-row="${k}"><strong>${MODULES[k].title}</strong><span class="muted small">${sub ?? MODULES[k].sub(genderOf(c))}</span>${betaTags(k)}${extra}</button>
    ${href ? html`<a class="nv-mo-gear" href="${href}" aria-label="Réglages · ${MODULES[k].title}" data-guide="mo-gear-${k}"><span class="ti ti-gear" aria-hidden="true"></span></a>` : ''}
    <button type="button" class="iv-switch" role="switch" aria-checked="${String(on)}" aria-label="${MODULES[k].title}" data-mo="${k}" ${guide ? html`data-guide="mo-switch"` : ''}></button>${foot ? html`<div class="as-foot-link">${foot}</div>` : ''}</li>`;
};

export async function renderModules(root: HTMLElement, ctx: Ctx, sub: string | undefined, onChange: () => void): Promise<void> {
  if (!isClubAdmin(ctx)) { mount(root, html`<section class="screen"><div class="empty"><p class="empty-title">Réservé au bureau du club.</p></div></section>`); return; }
  if (sub === 'choisir') return renderNeeds(root, ctx);
  if (sub === 'proposer') return renderProposal(root, ctx);
  if (sub === 'skins') return (await import('./skins-reglages')).renderClubSkins(root, ctx);   // D-157 : les skins offerts par le club
  if (sub === 'assistant') {   // D-158 : #/club/modules/assistant/<module>[/<étape>]
    const k = /\/assistant\/([a-z]+)/.exec(location.hash)?.[1] ?? '';
    if (hasWizard(k) && k !== 'site') return renderAssistant(root, ctx, k);
    location.replace('#/club/modules');
    return;
  }
  return renderClubModules(root, ctx, onChange);
}

// --- « Qu'est-ce qui prend le plus de temps au bureau ? » (premiers pas, maquette m1) -------------------------------
/** Les besoins cochés, gardés pendant la visite (m1 ↔ m2), club par club ; null : pas encore choisis. */
const NEEDS_KEY = (clubId: string) => `arceau.modules.besoins.${clubId}`;
const picks = new Map<string, Set<NeedKey>>();
const readNeeds = (clubId: string): Set<NeedKey> | null => {
  const kept = picks.get(clubId);
  if (kept) return kept;
  try { const v = sessionStorage.getItem(NEEDS_KEY(clubId)); return v ? new Set(JSON.parse(v) as NeedKey[]) : null; } catch { return null; }
};
const saveNeeds = (clubId: string, s: Set<NeedKey>) => {
  picks.set(clubId, s);
  try { sessionStorage.setItem(NEEDS_KEY(clubId), JSON.stringify([...s])); } catch { /* pour la visite */ }
};

/** Enregistre exactement ces modules (« Ce que le club utilise » coché), puis retourne à l'accueil. */
async function choose(ctx: Ctx, keys: ModuleKey[], btn: HTMLButtonElement, then?: () => void): Promise<void> {
  btn.disabled = true;
  try {
    // Les modules allumés par défaut (D-149) ne sont pas dans ces choix : ils restent comme ils sont.
    await chooseModules(ctx.club!.id, [...keys, ...DEFAULT_ON.filter(k => modOn(ctx, k) && !keys.includes(k))]);
    await refreshModules(ctx);
    forgetState();
    if (then) { then(); return; }
    toast(keys.length ? `C’est activé : ${keys.map(k => MODULES[k].title).join(', ')}` : 'C’est noté : tu ajouteras des modules quand tu veux, depuis Club.');
    location.hash = '#/';
  } catch { btn.disabled = false; toast('Enregistrement impossible.', 'ko'); }
}

function renderNeeds(root: HTMLElement, ctx: Ctx): void {
  const club = ctx.club!;
  const g = genderOf(ctx);
  const onNow = clubModuleKeys(ctx).filter(k => modOn(ctx, k));
  // Revenu ici : ses choix d'avant ; sinon les besoins dont tous les modules sont déjà activés.
  const picked = readNeeds(club.id) ?? new Set(NEEDS.filter(n => n.modules.every(k => onNow.includes(k))).map(n => n.key));
  const go = () => (picked.size ? `Voir ce qu’Arceau propose · ${picked.size} choix` : 'Coche ce qui pèse sur le bureau');
  mount(root, html`
    <section class="screen mo">
      <a class="back" href="#/">‹ Tes premiers pas</a>
      <header class="screen-head"><p class="eyebrow">${club.name}</p><h2>Qu’est-ce qui prend le plus de temps au bureau ?</h2>
        <p class="muted">Coche ce qui pèse sur le bureau : Arceau te propose de quoi t’en décharger. Tu pourras ajouter les autres modules ensuite, quand tu veux, depuis Club.</p></header>
      <ul class="mo-choices" data-guide="mo-choose">${NEEDS.map(n => html`
        <li><button type="button" class="card mo-choice ${picked.has(n.key) ? 'on' : ''}" data-need="${n.key}" aria-pressed="${String(picked.has(n.key))}">
          <span class="mo-tick" aria-hidden="true">${picked.has(n.key) ? '✓' : ''}</span>
          <span class="card-main"><strong>${n.title(g)}</strong><span class="muted small">${n.sub}</span></span>
          <span class="mo-emoji" aria-hidden="true">${n.icon}</span></button></li>`)}</ul>
      <button type="button" class="btn btn-primary mo-go" id="mo-see" ${picked.size ? '' : 'disabled'}>${go()}</button>
      <button type="button" class="btn btn-ghost mo-go" id="mo-none">${onNow.length ? 'Continuer avec ce qui est activé' : 'Continuer sans module pour l’instant'}</button>
      <section class="block"><div class="block-head"><h3>Dans tous les cas, tu as déjà</h3></div>${whyList(BASE_BENEFITS, 'mo-base')}</section>
    </section>`);
  for (const b of $$<HTMLButtonElement>(root, '[data-need]')) b.addEventListener('click', () => {
    const k = b.dataset.need as NeedKey;
    if (picked.has(k)) picked.delete(k); else picked.add(k);
    const on = picked.has(k);
    b.classList.toggle('on', on);
    b.setAttribute('aria-pressed', String(on));
    $(b, '.mo-tick')!.textContent = on ? '✓' : '';
    const see = $<HTMLButtonElement>(root, '#mo-see')!;
    see.textContent = go();
    see.disabled = !picked.size;
    saveNeeds(club.id, picked);
  });
  $(root, '#mo-see')!.addEventListener('click', () => { saveNeeds(club.id, picked); location.hash = '#/club/modules/proposer'; });
  $(root, '#mo-none')!.addEventListener('click', e => void choose(ctx, onNow, e.currentTarget as HTMLButtonElement));
}

// --- « Pour le club : N modules » (maquette m2) ----------------------------------------------------------------------
function renderProposal(root: HTMLElement, ctx: Ctx): void {
  const club = ctx.club!;
  const needs = readNeeds(club.id);
  if (!needs?.size) { location.replace('#/club/modules/choisir'); return; }
  const g = genderOf(ctx);
  const sections = proposal([...needs]);
  const proposed = sections.flatMap(s => s.modules);
  const others = clubModuleKeys(ctx).filter(k => !proposed.includes(k));
  const picked = new Set<ModuleKey>([...proposed, ...clubModuleKeys(ctx).filter(k => modOn(ctx, k))]);
  const setupSub = (k: ModuleKey) => `${cap(MODULES[k].setup.text)}.`;
  // D-158 : les modules à configurer (jamais activés) passent par leur assistant ; les autres s'activent comme avant.
  const toConfigure = () => clubModuleKeys(ctx).filter(k => picked.has(k) && needsWizard(ctx, k));
  const label = () => (toConfigure().length ? configureLabel(toConfigure().length) : activateLabel(picked.size));
  mount(root, html`
    <section class="screen mo">
      <a class="back" href="#/club/modules/choisir">‹ Ce qui prend du temps</a>
      <header class="screen-head"><p class="eyebrow">${club.name}</p><h2>Pour le club : ${proposed.length} ${proposed.length > 1 ? 'modules' : 'module'}</h2>
        <p class="muted">Activés d’un geste, éteints quand tu veux : rien n’est supprimé. Les autres restent dans Club, avec leur « Pourquoi », prêts à être ajoutés.</p></header>
      ${sections.map(s => html`
        <section class="block mo-obj">
          <div class="block-head"><h3>${s.need.short}</h3><span class="mo-pill">⏱ ${minutesLabel(s.min)}</span></div>
          ${s.modules.map(k => html`<ul class="mo-list">${modRow(ctx, k, picked.has(k), setupSub(k))}</ul>${whyList(MODULES[k].why(g))}`)}
        </section>`)}
      ${others.length ? html`<details class="alt-box"><summary>Les ${others.length} autres modules · à ajouter maintenant ou plus tard</summary>
        <ul class="mo-list">${others.map(k => modRow(ctx, k, picked.has(k)))}</ul></details>` : ''}
      <button type="button" class="btn btn-primary mo-go" id="mo-valid">${label()}</button>
    </section>`);
  const flip = (k: ModuleKey) => {
    if (picked.has(k)) picked.delete(k); else picked.add(k);
    for (const sw of $$<HTMLButtonElement>(root, `[data-mo="${k}"]`)) {
      sw.setAttribute('aria-checked', String(picked.has(k)));
      sw.closest('.mo-mod')?.classList.toggle('on', picked.has(k));
    }
    $(root, '#mo-valid')!.textContent = label();
  };
  for (const b of $$<HTMLButtonElement>(root, '[data-mo]')) b.addEventListener('click', () => flip(b.dataset.mo as ModuleKey));
  for (const b of $$<HTMLButtonElement>(root, '[data-mo-row]')) b.addEventListener('click', () => flip(b.dataset.moRow as ModuleKey));
  $(root, '#mo-valid')!.addEventListener('click', e => {
    const keys = clubModuleKeys(ctx).filter(k => picked.has(k));
    const wiz = toConfigure();
    if (!wiz.length) { void choose(ctx, keys, e.currentTarget as HTMLButtonElement); return; }
    // Le choix est noté, sans rien allumer de nouveau ; puis les assistants, le plus court d'abord.
    const order = chainOrder(wiz as WizKey[], () => sizeData(ctx));
    void choose(ctx, keys.filter(k => !wiz.includes(k)), e.currentTarget as HTMLButtonElement, () => {
      setChain(club.id, order.slice(1)); location.hash = `#/club/modules/assistant/${order[0]}`;
    });
  });
}

// --- Club › Modules, rangés par objectif (maquette m3) -------------------------------------------------------------
function renderClubModules(root: HTMLElement, ctx: Ctx, onChange: () => void): void {
  const club = ctx.club!;
  const st = modState(ctx);
  const rowOf = (k: ModuleKey) => st?.modules.find(m => m.module === k);
  const asks = (k: ModuleKey) => (st?.asks ?? []).filter(a => a.module === k).map(a => a.name);
  const since = (k: ModuleKey) => {
    const r = rowOf(k);
    if (!r) return DEFAULT_ON.includes(k) ? 'Allumé d’office pour tous les clubs' : '';
    if (r.origin === 'donnees') return 'Activé d’office : le club s’en servait déjà';
    if (r.origin === 'demo') return 'Activé pour la démo';
    return `Activé le ${dayFr(r.at)}${r.by ? ` par ${r.by}` : ''}`;
  };
  const extra = (k: ModuleKey) => {
    if (modOn(ctx, k)) { const s = since(k); return s ? html`<span class="mo-since">${s}</span>` : ''; }
    const a = asks(k);
    return a.length ? html`<span class="mo-ask">${a.slice(0, 2).join(' et ')} ${a.length > 1 ? 'aimeraient' : 'aimerait'} l’utiliser.</span>` : '';
  };
  const target = MODULE_KEYS.find(k => !modOn(ctx, k)) ?? MODULE_KEYS[0]!;   // la visite « Activer un module » : jamais une bêta
  // D-158 : la ligne d'un module, selon où il en est.
  const row = (k: ModuleKey): Html => {
    const w = wizardOf(ctx, k);
    const guide = k === target ? 'mo-switch' : `mo-wiz-${k}`;
    if (needsWizard(ctx, k) && w) {
      // Un assistant arrêté en route : où il en est, qui l'a commencé, « Reprendre ».
      const { n } = wizSize(ctx, k);
      return html`<li class="mo-mod as-off" data-mo-key="${k}">${icon(k)}
        <button type="button" class="card-main mo-main" data-wiz="${k}"><strong>${MODULES[k].title}</strong><span class="muted small">${MODULES[k].sub(genderOf(ctx))}</span>${betaTags(k)}
          <span class="as-run">${raw('<span class="ti as-i-pause" aria-hidden="true"></span>')}${runLine(w.step, n)}</span>
          <span class="as-mini" style="grid-template-columns:repeat(${n},minmax(0,1fr))" aria-hidden="true">${Array.from({ length: n }, (_, i) => html`<i class="${i < w.step - 1 ? 'd' : i === w.step - 1 ? 'c' : ''}"></i>`)}</span></button>
        <div class="as-act"><small>${startedLine(w)} · tes réponses sont gardées, rien n’est activé</small>
          <a class="btn btn-primary btn-sm" href="#/club/modules/assistant/${k}" data-guide="${guide}">Reprendre →</a></div></li>`;
    }
    if (needsWizard(ctx, k)) {
      // Jamais activé : plus d'interrupteur pour l'allumer ; « Configurer et activer » ouvre son assistant.
      const { q } = wizSize(ctx, k);
      return html`<li class="mo-mod as-off" data-mo-key="${k}">${icon(k)}
        <button type="button" class="card-main mo-main" data-wiz="${k}"><strong>${MODULES[k].title}</strong><span class="muted small">${MODULES[k].sub(genderOf(ctx))}</span>${betaTags(k)}${extra(k)}</button>
        <div class="as-act"><small>${raw('<span class="ti as-i-clock" aria-hidden="true"></span>')}${sizeLine(q)} · activé seulement à la fin</small>
          <a class="btn btn-primary btn-sm" href="#/club/modules/assistant/${k}" data-guide="${guide}">Configurer et activer</a></div></li>`;
    }
    const on = modOn(ctx, k);
    const revoir = on && k in WIZARDS ? html`<a class="as-revoir" href="#/club/modules/assistant/${k}" data-guide="${k === 'benevolat' ? 'mo-revoir' : `mo-revoir-${k}`}">${w ? `Revoir avec l’assistant · ${runLine(w.step, wizSize(ctx, k).n).toLowerCase()}` : 'Revoir avec l’assistant'} ›</a>` : '';
    return modRow(ctx, k, on, undefined, extra(k), k === target, true, revoir);
  };
  mount(root, html`
    <section class="screen mo">
      <a class="back" href="#/club">‹ Le club</a>
      <header class="screen-head"><p class="eyebrow">${club.name}</p><h2>Modules et réglages</h2>
        <p class="muted">Rangés par ce que le club veut faire. Un module s’active au bout de son assistant : quelques questions, chacune expliquée. La roue ouvre ses réglages ; l’interrupteur l’éteint.</p></header>
      <div class="screen" data-guide="mo-list">
        ${GOALS.filter(gl => gl.modules.length).map(gl => html`
          <h3 class="hub-sec mo-sec">${gl.title}</h3>${gl.base ? html`<p class="muted small mo-goal-base">${gl.base}</p>` : ''}
          <ul class="mo-list">${goalModules(ctx, gl).map(row)}</ul>`)}
      </div>
    </section>`);
  const open = (k: ModuleKey) => void openModuleSheet(ctx, k, !modOn(ctx, k), onChange);
  for (const b of $$<HTMLButtonElement>(root, '[data-mo]')) b.addEventListener('click', () => open(b.dataset.mo as ModuleKey));
  for (const b of $$<HTMLButtonElement>(root, '[data-mo-row]')) b.addEventListener('click', () => open(b.dataset.moRow as ModuleKey));
  for (const b of $$<HTMLButtonElement>(root, '[data-wiz]')) b.addEventListener('click', () => { location.hash = `#/club/modules/assistant/${b.dataset.wiz}`; });
}

// --- Les fiches « Activer » et « Éteindre » (maquette m4 ; a4) -----------------------------------------------------
const effects = (rows: [string, string][]) => html`<ul class="mo-effects" data-guide="mo-effects">${rows.map(([k, v]) => html`<li><b>${k}</b><span>${v}</span></li>`)}</ul>`;
const weekday = (d: string) => parseLocalDate(d).toLocaleDateString('fr-FR', { weekday: 'long' });

/** Ce qui apparaît (Activer), rôle par rôle, avec ce qui sera créé. */
function appearRows(ctx: Ctx, k: ModuleKey, pv: ModulePreview | null, resp: string | null = null): [string, string][] {
  const rows = MODULES[k].appears(genderOf(ctx)).map(r => [...r] as [string, string]);
  // B27 : le responsable des salles nommé (la fonction qui reçoit « Demandes de salle »), ou à confier.
  if (k === 'salles') {
    const i = rows.findIndex(r => r[0] === 'Responsable des salles');
    if (i >= 0) rows[i] = ['Responsable des salles', resp
      ? `Les mêmes écrans, même hors bureau : ${resp}, qui reçoit « Demandes de salle » (Club › Le bureau).`
      : 'Les mêmes écrans, même hors bureau : la personne à qui tu confieras « Demandes de salle » (Club › Le bureau). Personne pour l’instant : le bureau s’en occupe.'];
  }
  if (k === 'benevolat') rows.push(['Matchs', matchesLine(pv?.home_matches ?? 0, pv?.needs ?? [])]);
  if (k === 'page' && pv?.slug) {
    const at = HOST_BASE ? `${pv.slug}.${HOST_BASE}` : `/c/${pv.slug}`;
    rows[0] = ['Public', `La page du club en ligne à ${at} : matchs, résultats, équipes, contact ; aucune donnée personnelle.`];
  }
  return rows;
}

/** Ce qui disparaît, ce qui reste aux inscrits jusqu'à leur date, ce qui est gardé (Éteindre). */
function hideRows(k: ModuleKey, pv: ModulePreview | null): [string, string][] {
  const rows: [string, string][] = [['Disparaît', MODULES[k].hides]];
  if (k === 'benevolat') {
    const f = pv?.first;
    const line = keptLine(pv?.signed ?? 0, f ? { name: f.name, label: f.label, day: weekday(f.date) } : null);
    if (line) rows.push(['Reste', line]);
  }
  if (k === 'agenda' && pv?.signed) rows.push(['Reste', `${pv.signed > 1 ? `Les ${pv.signed} inscrits gardent leur place` : 'L’inscrit garde sa place'} jusqu’à la date de l’événement.`]);
  if (k === 'page' && pv?.followers) rows.push(['Reste', `${pv.followers > 1 ? `Les ${pv.followers} abonnés gardent leur compte` : 'L’abonné garde son compte'} ; plus rien ne leur est envoyé.`]);
  rows.push(['Gardé', MODULES[k].keeps]);
  return rows;
}

/** « ⏱ Mise en place : 5 min · les créneaux des 12 matchs à domicile se créent tout seuls », puis ce que ça demande (B26 : le
 *  même format que « Pour le club », minutesLabel). */
function setupLines(k: ModuleKey, pv: ModulePreview | null): Html {
  const m = MODULES[k];
  const text = k === 'benevolat' ? volunteerSetup(pv?.home_matches ?? 0) : m.setup.text;
  return html`<p class="mo-pill">⏱ Mise en place : ${minutesLabel(m.setup.min)} · ${text}</p>
    <p class="muted small mo-asks">Ensuite, ça te demande : ${m.asks.charAt(0).toLowerCase()}${m.asks.slice(1)}</p>`;
}

export async function openModuleSheet(ctx: Ctx, k: ModuleKey, turnOn: boolean, onDone: () => void): Promise<void> {
  const club = ctx.club;
  if (!club) return;
  const m = MODULES[k];
  const [pv, bureau] = await Promise.all([modulePreview(club.id, k).catch(() => null),
    k === 'salles' && turnOn ? clubBureau(club.id).catch(() => null) : Promise.resolve(null)]);
  const missing = DEFAULT_REWARDS.filter(r => pv?.rewards_missing?.includes(r.grade));
  // Qui tient la fonction qui reçoit « Demandes de salle » (B27) : « Karim (Responsable des salles) ».
  const respFns = (bureau?.functions ?? []).filter(f => f.receives.includes('salles') && f.holders.length);
  const resp = respFns.length ? respFns.map(f => `${f.holders.map(h => h.short || h.full_name).join(' et ')} (${f.holders[0]!.title || f.name})`).join(', ') : null;
  const body = turnOn ? html`
    <div class="form">
      ${m.beta ? html`<p class="fo-row"><span class="fo-beta">Bêta</span><span class="muted small">Réservée au club pilote pendant la bêta · ${m.beta.price.charAt(0).toLowerCase()}${m.beta.price.slice(1)}</span></p>` : ''}
      <p class="eyebrow eyebrow-accent mo-eyebrow">Pourquoi l’activer</p>
      ${whyList(m.why(genderOf(ctx)), 'mo-why')}
      ${setupLines(k, pv)}
      <p class="eyebrow eyebrow-accent mo-eyebrow">Ce qui apparaît</p>
      ${effects(appearRows(ctx, k, pv, resp))}
      ${k === 'formation' ? html`<p class="hint">Les matchs tenus comptent tout seuls grâce au Bénévolat (parcours Table de marque et Arbitrage).</p>` : ''}
      ${k === 'avantages' && missing.length && modOn(ctx, 'benevolat') ? html`
        <label class="check"><input type="checkbox" id="mo-rewards"><span><strong>Proposer les récompenses des grades</strong><br>
          <span class="muted small">Modifiables ensuite ; rien n’est promis tant que tu ne les reprends pas.</span>
          <ul class="mo-rewards">${missing.map(r => html`<li>${r.label} : ${r.title}</li>`)}</ul></span></label>` : ''}
      ${m.bureauOnly || !pv?.notice ? '' : html`
        <label class="check"><input type="checkbox" id="mo-notify" checked><span><strong>Prévenir les membres</strong><br>
          <span class="muted small">« ${pv.notice.body} »</span></span></label>`}
      <button type="button" class="btn btn-primary" id="mo-do" data-guide="mo-do">Activer ${m.noun}</button>
      ${k in WIZARDS ? html`<a class="as-revoir" href="#/club/modules/assistant/${k}">Revoir avec l’assistant : ses réglages, question par question ›</a>` : ''}
      ${filmBtn(k)}
    </div>` : html`
    <div class="form">
      ${effects(hideRows(k, pv))}
      <button type="button" class="btn btn-danger-ghost" id="mo-do" data-guide="mo-do">Éteindre ${m.noun}</button>
    </div>`;
  openSheet(`${turnOn ? 'Activer' : 'Éteindre'} ${m.noun}`, body, (sheet, close) => {
    // La fiche s'ouvre sur « Pourquoi l'activer » (m4), pas sur la case « Prévenir les membres » qui prend le focus.
    requestAnimationFrame(() => { sheet.scrollTop = 0; });
    $(sheet, '[data-film]')?.addEventListener('click', () => { close(); openFilm(k); });
    $(sheet, '#mo-do')!.addEventListener('click', async (e) => {
      const btn = e.currentTarget as HTMLButtonElement;
      btn.disabled = true;
      try {
        const notify = turnOn && !!$<HTMLInputElement>(sheet, '#mo-notify')?.checked;
        const rewards = turnOn && !!$<HTMLInputElement>(sheet, '#mo-rewards')?.checked;
        const r = await setModule(club.id, k, turnOn, notify, { rewards });
        await refreshModules(ctx);
        forgetState();
        close();
        toast(turnOn ? `C’est activé : ${m.title}${r.notified ? ' · les membres sont prévenus' : ''}` : `C’est éteint : ${m.title}. Rien n’est supprimé.`);
        onDone();
      } catch { btn.disabled = false; toast('Impossible pour le moment.', 'ko'); }
    });
  });
}

/** « ▶ Voir le film » : l'animation filmée sur la démo (celle du tuto du module). */
export function openFilm(k: ModuleKey): void {
  const f = MODULES[k].film;
  if (!f || !FILMS[f]) return;
  openSheet(`${MODULES[k].title} · le film`, html`
    <div class="mo-film-sheet">
      <span class="gd-phone on"><img src="/guide/${f}.webp" alt="${MODULES[k].title}, filmé sur le club de démo"></span>
      <p class="muted small">Filmé sur le club de démo · ${FILMS[f]} s, en boucle.</p>
    </div>`);
}

// --- La ligne « Activer » d'un module éteint, dans son objectif (onglet Club du bureau, maquette m6) ---------------
/** Un module éteint, là où il servirait : son pourquoi en une phrase, « Activer » (la fiche dit d'abord pourquoi, puis
 *  ce qui apparaît). Les demandes des coachs s'y lisent. */
export function offModuleLine(ctx: Ctx, k: ModuleKey): Html {
  const a = (modState(ctx)?.asks ?? []).filter(x => x.module === k).map(x => x.name);
  return html`<div class="card mo-offline" data-guide="mo-off-${k}">${icon(k)}
    <span class="card-main"><strong>${MODULES[k].title}</strong><span class="muted small">${MODULES[k].sub(genderOf(ctx))}</span>
      ${a.length ? html`<span class="mo-ask small">${a.slice(0, 2).join(' et ')} ${a.length > 1 ? 'aimeraient' : 'aimerait'} l’utiliser.</span>` : ''}</span>
    <button type="button" class="btn btn-ghost btn-sm" data-mo-on="${k}">${needsWizard(ctx, k) ? (wizardOf(ctx, k) ? 'Reprendre' : 'Configurer et activer') : 'Activer'}</button></div>`;
}

/** Les boutons « Activer » et « ▶ Voir le film » d'un écran. */
export function wireModuleButtons(root: HTMLElement, ctx: Ctx, onChange: () => void): void {
  for (const b of $$<HTMLButtonElement>(root, '[data-mo-on]')) b.addEventListener('click', () => activateModule(ctx, b.dataset.moOn as ModuleKey, onChange));
  for (const b of $$<HTMLButtonElement>(root, '[data-film]')) b.addEventListener('click', () => openFilm(b.dataset.film as ModuleKey));
}

// --- L'espace d'un partenaire quand le club a éteint ses partenaires (vouvoiement, D-074) ---------------------------
export function renderPartnerPaused(root: HTMLElement, ctx: Ctx): void {
  mount(root, html`
    <section class="screen">
      <header class="screen-head"><p class="eyebrow">${ctx.club?.name ?? ''}</p><h2>Votre espace partenaire</h2></header>
      <div class="empty mo-off" data-guide="mo-off">
        ${icon('partenaires')}
        <p class="empty-title">Le club n’utilise pas l’espace des partenaires pour l’instant.</p>
        <p>Votre fiche et vos demandes sont gardées telles quelles : tout revient dès que le club le rouvre.</p>
      </div>
    </section>`);
}

// --- Un module éteint, atteint par un vieux lien ------------------------------------------------------------------
/** Jamais une erreur : « Ce module n'est pas utilisé par le club », son pourquoi et « Activer » pour le bureau,
 *  « Demander au bureau » pour un coach (une demande, pas une activation). */
export function renderModuleOff(root: HTMLElement, ctx: Ctx, k: ModuleKey, onChange: () => void): void {
  const m = MODULES[k];
  const admin = isClubAdmin(ctx);
  const coach = !admin && (ctx.full.coachOf.size > 0 || ctx.roles.some(r => r.role === 'technical' && r.club_id === ctx.club?.id));
  mount(root, html`
    <section class="screen">
      <header class="screen-head"><p class="eyebrow">${ctx.club?.name ?? ''}</p><h2>${m.title}</h2></header>
      <div class="empty mo-off" data-guide="mo-off">
        ${icon(k)}
        <p class="empty-title">Ce module n’est pas utilisé par le club.</p>
        <p>${m.sub(genderOf(ctx))}</p>
        ${admin ? html`<span class="btn-row"><button type="button" class="btn btn-primary" data-mo-on="${k}">${needsWizard(ctx, k) ? 'Configurer et activer' : 'Activer'}</button>${filmBtn(k)}</span>
            <p class="hint">${needsWizard(ctx, k) ? 'Un assistant te pose quelques questions, chacune expliquée ; le module s’active seulement à la fin.' : 'Une fiche te dit d’abord pourquoi l’activer, puis ce qui apparaît, et pour qui.'}</p>`
          : coach ? html`<span class="btn-row"><button type="button" class="btn btn-primary" id="mo-ask">Demander au bureau</button></span>
            <p class="hint">Le bureau est prévenu ; c’est lui qui l’active pour le club.</p>`
          : html`<span class="btn-row"><a class="btn btn-ghost" href="#/">Retour à l’accueil</a></span>`}
      </div>
      ${admin || coach ? html`<section class="block"><p class="eyebrow eyebrow-accent mo-eyebrow">Pourquoi l’activer</p>${whyList(m.why(genderOf(ctx)))}</section>` : ''}
    </section>`);
  wireModuleButtons(root, ctx, onChange);
  $(root, '#mo-ask')?.addEventListener('click', async (e) => {
    const b = e.currentTarget as HTMLButtonElement;
    b.disabled = true;
    try {
      const sent = await askModule(ctx.club!.id, k);
      b.textContent = sent ? 'Demandé ✓' : 'Déjà demandé';
      toast(sent ? 'C’est demandé : le bureau est prévenu.' : 'Déjà demandé cette semaine : le bureau est prévenu.');
    } catch { b.disabled = false; toast('Impossible pour le moment.', 'ko'); }
  });
}
